// Human-readable language labels for the player's audio and subtitle controls.
//
// Hosts hand us whatever their encoder had lying around. A LuluStream HLS
// master (Thudakkam) declares its five renditions as
//
//   #EXT-X-MEDIA:TYPE=AUDIO,NAME="தமிழ்",LANGUAGE="ta",DEFAULT=YES,…
//
// so the settings sheet would otherwise list தமிழ் / తెలుగు / हिन्दी / മലയാളം /
// ಕನ್ನಡ — five scripts nobody can scan. Everything a viewer sees resolves
// through here to one Latin name, and the same function decides which
// rendition is the film's own language.

// ISO 639-1 (plus a few 639-2 codes) → English name.
const ISO_NAMES = {
  as: "Assamese", bn: "Bengali", gu: "Gujarati", hi: "Hindi", kn: "Kannada",
  kok: "Konkani", ml: "Malayalam", mr: "Marathi", ne: "Nepali", or: "Odia",
  pa: "Punjabi", sa: "Sanskrit", sd: "Sindhi", si: "Sinhala", ta: "Tamil",
  te: "Telugu", ur: "Urdu",
  ar: "Arabic", az: "Azerbaijani", be: "Belarusian", bg: "Bulgarian",
  bs: "Bosnian", ca: "Catalan", cs: "Czech", cy: "Welsh", da: "Danish",
  de: "German", el: "Greek", en: "English", es: "Spanish", et: "Estonian",
  eu: "Basque", fa: "Persian", fi: "Finnish", fil: "Filipino", fr: "French",
  ga: "Irish", gl: "Galician", he: "Hebrew", hr: "Croatian", hu: "Hungarian",
  hy: "Armenian", id: "Indonesian", is: "Icelandic", it: "Italian",
  ja: "Japanese", ka: "Georgian", kk: "Kazakh", km: "Khmer", ko: "Korean",
  ky: "Kyrgyz", lo: "Lao", lt: "Lithuanian", lv: "Latvian", mk: "Macedonian",
  mn: "Mongolian", ms: "Malay", my: "Burmese", nb: "Norwegian", nl: "Dutch",
  no: "Norwegian", pl: "Polish", ps: "Pashto", pt: "Portuguese", ro: "Romanian",
  ru: "Russian", sk: "Slovak", sl: "Slovenian", sq: "Albanian", sv: "Swedish",
  sw: "Swahili", tg: "Tajik", th: "Thai", tl: "Filipino", tr: "Turkish",
  tt: "Tatar", uk: "Ukrainian", uz: "Uzbek", vi: "Vietnamese", zh: "Chinese",
  zu: "Zulu",
};

// Names a host may already send (its own or another site's spelling), mapped to
// the one form we show. Keys are compared diacritic- and case-insensitively.
const NAME_ALIASES = {
  tamil: "Tamil", "tamizh": "Tamil", telugu: "Telugu", hindi: "Hindi",
  malayalam: "Malayalam", malyalam: "Malayalam", kannada: "Kannada",
  bengali: "Bengali", bangla: "Bengali", marathi: "Marathi", punjabi: "Punjabi",
  panjabi: "Punjabi", gujarati: "Gujarati", oriya: "Odia", odia: "Odia",
  assamese: "Assamese", urdu: "Urdu", sinhala: "Sinhala", nepali: "Nepali",
  english: "English", eng: "English", arabic: "Arabic", french: "French",
  spanish: "Spanish", german: "German", japanese: "Japanese", korean: "Korean",
  chinese: "Chinese", mandarin: "Chinese", russian: "Russian",
  portuguese: "Portuguese", italian: "Italian", turkish: "Turkish",
  thai: "Thai", vietnamese: "Vietnamese", indonesian: "Indonesian",
  malay: "Malay", filipino: "Filipino", tagalog: "Filipino", dutch: "Dutch",
  polish: "Polish", swedish: "Swedish", danish: "Danish", norwegian: "Norwegian",
  finnish: "Finnish", greek: "Greek", hebrew: "Hebrew", persian: "Persian",
  ukrainian: "Ukrainian", romanian: "Romanian", czech: "Czech",
  hungarian: "Hungarian", burmese: "Burmese", khmer: "Khmer", lao: "Lao",
};

// When a rendition declares no LANGUAGE at all, the script of its NAME is the
// only clue left. Ranges are the Unicode blocks of the languages this library
// actually carries; order matters only for overlapping blocks (none here).
const SCRIPT_RANGES = [
  [0x0b80, 0x0bff, "Tamil"],
  [0x0c00, 0x0c7f, "Telugu"],
  [0x0c80, 0x0cff, "Kannada"],
  [0x0d00, 0x0d7f, "Malayalam"],
  [0x0980, 0x09ff, "Bengali"],
  [0x0a00, 0x0a7f, "Punjabi"],
  [0x0a80, 0x0aff, "Gujarati"],
  [0x0b00, 0x0b7f, "Odia"],
  [0x0d80, 0x0dff, "Sinhala"],
  [0x0900, 0x097f, "Hindi"], // Devanagari — Hindi unless a code says Marathi
  [0x0600, 0x06ff, "Arabic"],
  [0x0590, 0x05ff, "Hebrew"],
  [0x0e00, 0x0e7f, "Thai"],
  [0xac00, 0xd7af, "Korean"],
  [0x3040, 0x30ff, "Japanese"],
  [0x4e00, 0x9fff, "Chinese"],
];

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

function fromScript(name) {
  for (const ch of String(name || "")) {
    const code = ch.codePointAt(0);
    for (const [lo, hi, label] of SCRIPT_RANGES) {
      if (code >= lo && code <= hi) return label;
    }
  }
  return null;
}

// One canonical English name for a track, given whatever it carries.
export function languageName(code, name) {
  const key = String(code || "").trim().toLowerCase().split(/[-_]/)[0];
  if (key && ISO_NAMES[key]) return ISO_NAMES[key];
  // Admin-mapped tracks store the full name in `code` as often as a code, so
  // the alias table gets first look before we fall through to the name.
  if (key && NAME_ALIASES[key]) return NAME_ALIASES[key];
  const raw = String(name || "").trim();
  if (!raw) return key ? key.toUpperCase() : "";
  const alias = NAME_ALIASES[normalize(raw)];
  if (alias) return alias;
  const script = fromScript(raw);
  if (script) return script;
  // Unknown but ASCII: show it as given, capitalised, rather than hiding it.
  return /^[\x20-\x7f]+$/.test(raw)
    ? raw.replace(/\b\w/g, (c) => c.toUpperCase())
    : raw;
}

// What the settings sheet shows for one audio/subtitle rendition.
export function languageLabel(code, name) {
  const label = languageName(code, name);
  if (label) return label;
  return code ? String(code).toUpperCase() : "Audio";
}

// Index of the rendition matching `target` (an ISO code like "ml" or a name
// like "Malayalam"), or -1 when the list has no such track. Indexes line up
// with the manifest order, so the result is directly usable as an audioTrack.
export function findLanguageIndex(tracks, target) {
  if (!target || !Array.isArray(tracks) || tracks.length === 0) return -1;
  const want = languageName(target, target);
  if (!want) return -1;
  const wanted = normalize(want);
  for (let i = 0; i < tracks.length; i += 1) {
    const t = tracks[i] || {};
    const label = languageName(t.code || t.lang, t.name || t.label);
    if (label && normalize(label) === wanted) return i;
  }
  // Last resort: the raw code the host sent, compared literally ("ml" vs "ml").
  const code = String(target).trim().toLowerCase();
  return tracks.findIndex((t) => String((t && (t.code || t.lang)) || "").toLowerCase() === code);
}

// True when this rendition IS the film's own language — the sheet tags it so
// "why is this one playing?" answers itself.
export function isOriginalLanguage(track, target) {
  if (!track || !target) return false;
  const want = normalize(languageName(target, target));
  const label = normalize(languageName(track.code || track.lang, track.name || track.label));
  return Boolean(want) && want === label;
}
